import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate } from 'react-router-dom';
import {
  Users, Building2, Shield, Activity,
  CheckCircle2, Loader2, Layers,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { AuditLogTab } from '../components/admin/AuditLogTab';
import EmailChannelHealthTile from '../components/admin/EmailChannelHealthTile';
import PendingInvitesTile from '../components/admin/PendingInvitesTile';
import FailedLoginActivityTile from '../components/admin/FailedLoginActivityTile';
import RecentAdminActivityStrip from '../components/admin/RecentAdminActivityStrip';
import UnusualLoginsStrip from '../components/admin/UnusualLoginsStrip';
import { ShieldCheck, UserPlus, Gauge, Clock } from 'lucide-react';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import EditUserDialog from '../components/admin/EditUserDialog';
import InviteUserDialog from '../components/admin/InviteUserDialog';
import CreateOrgDialog from '../components/admin/CreateOrgDialog';
import CreateUserDialog from '../components/admin/CreateUserDialog';
import SeedDemoTile from '../components/admin/SeedDemoTile';
import UserDetailSlideOver from '../components/admin/UserDetailSlideOver';
import AdminActivityTimelineOverlay from '../components/admin/AdminActivityTimelineOverlay';
import ChangeRoleDialog from '../components/admin/ChangeRoleDialog';
import UsersTab from '../components/admin/tabs/UsersTab';
import RolesTab from '../components/admin/tabs/RolesTab';
import ModulesTab from '../components/admin/tabs/ModulesTab';
import OrgManagementView from '../components/admin/tabs/OrgManagementView';
import { ROLE_COLORS, ROLE_NAMES } from '../components/admin/adminConstants';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { startImpersonation, isSuperAdmin: authIsSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [currentUser, setCurrentUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditStats, setAuditStats] = useState({});
  const [auditCategories, setAuditCategories] = useState({});
  const [auditSearch, setAuditSearch] = useState('');
  const [auditCategory, setAuditCategory] = useState('all');
  const [auditDateRange, setAuditDateRange] = useState('7d');
  const [auditPage, setAuditPage] = useState(0);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [managingOrgId, setManagingOrgId] = useState(null);
  // Transient highlight on the Organizations tab. When an Org Admin
  // jumps from the Create/Invite dialog via the Org breadcrumb, we
  // briefly pulse the matching org card so their eye finds it.
  const [pulsingOrgId, setPulsingOrgId] = useState(null);
  // Same pattern, applied to the Users tab — when an admin force-signs
  // out a teammate via the slide-over, briefly pulse that user's row
  // so the action visibly lands in the list view.
  const [pulsingUserId, setPulsingUserId] = useState(null);
  // When an admin saves Org Settings, capture the orgId so we can
  // pulse the matching card the moment they navigate back to the
  // Organizations list (spatial confirmation that the save landed
  // on the right tenant).
  const [lastSavedOrgId, setLastSavedOrgId] = useState(null);
  // Single helper used by every admin write-action (force-signout,
  // role change, edit save) so the row-pulse feedback stays
  // consistent. Switches to the Users tab so the pulse is in view.
  const pulseUserRow = useCallback((userId) => {
    if (!userId) return;
    setActiveTab('users');
    setPulsingUserId(userId);
    setTimeout(() => setPulsingUserId(null), 2500);
  }, []);
  const [viewingUser, setViewingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'user', organization_id: '', newPassword: '', notifications: {} });
  const [editSaving, setEditSaving] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState(null);
  const [newRoleValue, setNewRoleValue] = useState('user');
  
  // Form states
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'user', department_id: '', organization_id: '' });
  const [orgForm, setOrgForm] = useState({ name: '', description: '' });
  const [userForm, setUserForm] = useState({ email: '', name: '', password: '', role: 'user', organization_id: '', send_invitation: true });
  const [deleteOrgDialogOpen, setDeleteOrgDialogOpen] = useState(false);
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState('');
  const [deletingOrg, setDeletingOrg] = useState(false);
  
  // Fetch current user's RBAC info
  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
        return data;
      }
    } catch (error) {
      logger.error('Error fetching user info:', error);
    }
    return null;
  }, []);
  
  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/organizations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      logger.error('Error fetching organizations:', error);
    }
  }, []);
  
  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      let url = `${API_URL}/api/rbac/users`;
      const params = new URLSearchParams();
      
      if (selectedOrg !== 'all') {
        params.append('organization_id', selectedOrg);
      }
      if (roleFilter !== 'all') {
        params.append('role', roleFilter);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  }, [selectedOrg, roleFilter]);
  
  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (overrides = {}) => {
    try {
      const token = secureStorage.getItem('session_token');
      const page = overrides.page ?? auditPage;
      const category = overrides.category ?? auditCategory;
      const search = overrides.search ?? auditSearch;
      const dateRange = overrides.dateRange ?? auditDateRange;
      
      let url = `${API_URL}/api/rbac/audit-logs?limit=50&offset=${page * 50}`;
      
      if (selectedOrg !== 'all') {
        url += `&organization_id=${selectedOrg}`;
      }
      if (category !== 'all') {
        url += `&category=${category}`;
      }
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      // Date range
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate = new Date();
        if (dateRange === '24h') startDate.setHours(now.getHours() - 24);
        else if (dateRange === '7d') startDate.setDate(now.getDate() - 7);
        else if (dateRange === '30d') startDate.setDate(now.getDate() - 30);
        else if (dateRange === '90d') startDate.setDate(now.getDate() - 90);
        url += `&start_date=${startDate.toISOString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
        setAuditTotal(data.total || 0);
        if (data.stats) setAuditStats(data.stats);
      }
    } catch (error) {
      logger.error('Error fetching audit logs:', error);
    }
  }, [selectedOrg, auditPage, auditCategory, auditSearch, auditDateRange]);
  
  // Fetch audit categories
  const fetchAuditCategories = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/audit-logs/actions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditCategories(data.categories || {});
      }
    } catch (error) {
      logger.error('Error fetching audit categories:', error);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const user = await fetchCurrentUser();
      
      if (!user) {
        toast.error('Please log in to access admin features');
        navigate('/login');
        return;
      }
      
      // Check if user has admin permissions
      const isAdmin = user.is_super_admin || user.permissions?.some(p => 
        p.includes('users.') || p.includes('orgs.')
      );
      
      if (!isAdmin) {
        toast.error('You do not have permission to access this page');
        navigate('/');
        return;
      }
      
      await Promise.all([
        fetchOrganizations(),
        fetchUsers(),
        fetchAuditLogs(),
        fetchAuditCategories(),
      ]);
      
      setLoading(false);
    };
    
    loadData();
  }, [fetchCurrentUser, fetchOrganizations, fetchUsers, fetchAuditLogs, fetchAuditCategories, navigate]);
  
  // Refetch users when filters change
  useEffect(() => {
    if (!loading) {
      fetchUsers();
    }
  }, [selectedOrg, roleFilter, fetchUsers, loading]);
  
  // Invite user
  const handleInviteUser = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(inviteForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Invitation sent to ${inviteForm.email}`);
        setInviteDialogOpen(false);
        setInviteForm({ email: '', role: 'user', department_id: '', organization_id: '' });
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Error sending invitation');
    }
  };
  
  // Create organization
  const handleCreateOrg = async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orgForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Organization "${orgForm.name}" created — launching onboarding wizard`);
        setCreateOrgDialogOpen(false);
        setOrgForm({ name: '', description: '' });
        await fetchOrganizations();
        const newOrgId = data?.id || data?.organization?.id;
        if (newOrgId) {
          // Start the onboarding session + navigate into the wizard.
          // The wizard endpoint is idempotent — safe to call even if
          // the server already created the session row.
          try {
            await authedFetch(`${API_URL}/api/onboarding/start`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ org_id: newOrgId }),
            });
          } catch { /* non-fatal */ }
          navigate(`/admin/onboarding/${newOrgId}`);
        }
      } else {
        toast.error(data.detail || 'Failed to create organization');
      }
    } catch (error) {
      toast.error('Error creating organization');
    }
  };
  
  // Delete organization
  const handleDeleteOrg = async () => {
    if (!managingOrgId) return;
    const orgName = organizations.find(o => o.id === managingOrgId)?.name || '';
    if (deleteOrgConfirmText !== orgName) {
      toast.error('Organization name does not match. Please type the exact name to confirm.');
      return;
    }
    setDeletingOrg(true);
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/organizations/${managingOrgId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`Organization "${orgName}" has been deleted`);
        setDeleteOrgDialogOpen(false);
        setDeleteOrgConfirmText('');
        setManagingOrgId(null);
        fetchOrganizations();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete organization');
      }
    } catch (error) {
      toast.error('Error deleting organization');
    } finally {
      setDeletingOrg(false);
    }
  };

  // Create user directly
  const handleCreateUser = async () => {
    try {
      const response = await api.post('/rbac/users/create', userForm);
      const wasEmailed = response.data?.invitation_sent;
      const sendOpted = userForm.send_invitation !== false;
      let blurb;
      if (sendOpted && wasEmailed) {
        blurb = `Invitation emailed to ${userForm.email}. They'll be forced to set their own password on first sign-in.`;
      } else if (sendOpted && !wasEmailed) {
        blurb = `User "${userForm.email}" created. Email delivery skipped (Resend not configured) — share the temp password manually.`;
      } else {
        blurb = `User "${userForm.email}" created with temporary password. Share it with them directly.`;
      }
      toast.success(blurb);
      setCreateUserDialogOpen(false);
      setUserForm({ email: '', name: '', password: '', role: 'user', organization_id: '', send_invitation: true });
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to create user');
      toast.error(msg);
    }
  };
  
  // Resend invitation — re-mints a temp password (24h TTL) and
  // re-emails the user via Resend. Falls back gracefully when the
  // backend can't deliver (admin sees the new temp password in the
  // toast so they can share it manually).
  const handleResendInvitation = async (targetUser) => {
    if (!targetUser?.user_id) return;
    if (!confirm(
      `Resend invitation to ${targetUser.email}? `
      + `This will generate a new temporary password and invalidate any existing session.`
    )) {
      return;
    }
    try {
      const response = await api.post(`/rbac/users/${targetUser.user_id}/resend-invitation`);
      const { invitation_sent: sent, temporary_password: tempPwd } = response.data || {};
      if (sent) {
        toast.success(`Invitation re-sent to ${targetUser.email}.`, {
          description: 'Temporary password expires in 24 hours.',
        });
      } else {
        toast.warning(
          `Invitation generated but email delivery is unavailable. New temp password: ${tempPwd}`,
          { duration: 12000 },
        );
      }
      fetchUsers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : 'Failed to resend invitation');
      toast.error(msg);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId, email) => {
    if (!confirm(`Are you sure you want to delete user ${email}?`)) return;
    
    try {
      const token = secureStorage.getItem('session_token');
      const response = await authedFetch(`${API_URL}/api/rbac/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Error deleting user');
    }
  };
  
  const handleChangeRole = async () => {
    if (!changeRoleUser) return;
    try {
      const token = secureStorage.getItem('session_token');
      const orgId = changeRoleUser.roles?.[0]?.organization_id || organizations[0]?.id;
      
      // First revoke current role, then assign new one
      const currentRole = changeRoleUser.roles?.[0]?.role || 'user';
      if (currentRole !== newRoleValue) {
        // Revoke old role
        await authedFetch(`${API_URL}/api/rbac/users/${changeRoleUser.user_id}/roles/${currentRole}?organization_id=${orgId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Assign new role
        const response = await authedFetch(`${API_URL}/api/rbac/users/${changeRoleUser.user_id}/roles`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: newRoleValue, organization_id: orgId })
        });
        if (response.ok) {
          toast.success(`Role updated to ${ROLE_NAMES[newRoleValue]}`);
          const changedUserId = changeRoleUser.user_id;
          setChangeRoleUser(null);
          fetchUsers();
          pulseUserRow(changedUserId);
        } else {
          const data = await response.json();
          toast.error(data.detail || 'Failed to change role');
        }
      } else {
        toast.info('Role unchanged');
        setChangeRoleUser(null);
      }
    } catch (error) {
      toast.error('Error changing role');
    }
  };
  
  const openEditUser = (user) => {
    setEditForm({
      name: user.name || '',
      role: user.roles?.[0]?.role || 'user',
      organization_id: user.roles?.[0]?.organization_id || organizations[0]?.id || '',
      newPassword: '',
      notifications: user.profile?.notifications || { email: true, lei_alerts: true, weekly_reports: false }
    });
    setEditingUser(user);
  };
  
  const handleEditUser = async () => {
    if (!editingUser) return;
    setEditSaving(true);
    try {
      const token = secureStorage.getItem('session_token');
      const body = {};
      
      // Name
      if (editForm.name !== (editingUser.name || '')) {
        body.name = editForm.name;
      }
      // Role
      const currentRole = editingUser.roles?.[0]?.role || 'user';
      if (editForm.role !== currentRole) {
        body.role = editForm.role;
      }
      // Organization
      const currentOrg = editingUser.roles?.[0]?.organization_id || '';
      if (editForm.organization_id !== currentOrg) {
        body.organization_id = editForm.organization_id;
      }
      // Password reset
      if (editForm.newPassword) {
        body.new_password = editForm.newPassword;
      }
      // Notifications
      body.notifications = editForm.notifications;
      
      const response = await authedFetch(`${API_URL}/api/rbac/users/${editingUser.user_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        toast.success('User updated successfully');
        const editedUserId = editingUser.user_id;
        setEditingUser(null);
        fetchUsers();
        pulseUserRow(editedUserId);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Error updating user');
    } finally {
      setEditSaving(false);
    }
  };
  
  // Filter users by search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(term) ||
      user.name?.toLowerCase().includes(term)
    );
  });
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 text-teal-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  const theme = getLightSectionTheme('Navy');

  const heroStats = [
    {
      label: 'Total Users',
      value: users.length,
      testId: 'admin-stat-users',
    },
    {
      label: 'Organizations',
      value: organizations.length,
      testId: 'admin-stat-orgs',
    },
    {
      label: 'Super Admins',
      value: users.filter(u => u.roles?.some(r => r.role === 'super_admin')).length,
      testId: 'admin-stat-super-admins',
    },
    {
      label: 'Audit Events',
      value: auditTotal,
      testId: 'admin-stat-audit-events',
    },
  ];

  /**
   * Open the "Create User" dialog. For org admins we pre-seed the
   * org_id to their primary org so the request never lands in
   * "no organization specified" 400s, and the dialog renders a
   * read-only org badge instead of the picker.
   *
   * Note: `/rbac/me` returns `organizations` as an array of FULL
   * org objects ({id, name, ...}), not raw ids — pluck `.id`.
   */
  const openCreateUserDialog = () => {
    const defaultOrgId = currentUser?.is_super_admin
      ? ''
      : (currentUser?.organizations?.[0]?.id || '');
    setUserForm({
      email: '', name: '', password: '', role: 'user',
      organization_id: defaultOrgId,
      send_invitation: true,
    });
    setCreateUserDialogOpen(true);
  };

  /**
   * Open the "Invite User" dialog. Same pre-seed treatment so org
   * admins land in the dialog with their org already locked in.
   */
  const openInviteDialog = () => {
    const defaultOrgId = currentUser?.is_super_admin
      ? ''
      : (currentUser?.organizations?.[0]?.id || '');
    setInviteForm({
      email: '', role: 'user', department_id: '',
      organization_id: defaultOrgId,
    });
    setInviteDialogOpen(true);
  };

  const heroActions = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        onClick={() => setTimelineOpen(true)}
        size="sm"
        variant="ghost"
        className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
        data-testid="admin-open-timeline-btn"
      >
        <Clock className="h-4 w-4 mr-2" /> Timeline
      </Button>
      <Button
        onClick={() => navigate('/admin/telemetry')}
        size="sm"
        variant="ghost"
        className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
        data-testid="admin-view-telemetry-btn"
      >
        <Gauge className="h-4 w-4 mr-2" /> Telemetry
      </Button>
      <EmailChannelHealthTile
        isSuperAdmin={!!currentUser?.is_super_admin}
        compact
      />
      {!!currentUser?.is_super_admin && (
        <Button
          onClick={() => setCreateOrgDialogOpen(true)}
          size="sm"
          className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="admin-create-org-btn"
        >
          <Building2 className="h-4 w-4 mr-2" /> New Org
        </Button>
      )}
      {/* "New User" — visible to BOTH super admins and org admins.
          Org admins land here pre-scoped to their own organization
          (see openCreateUserDialog below); super admins still pick
          the target org from the in-dialog selector. */}
      {(currentUser?.is_super_admin
        || currentUser?.permissions?.includes('users.create.org')) && (
        <Button
          onClick={openCreateUserDialog}
          size="sm"
          className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="admin-create-user-btn"
        >
          <Users className="h-4 w-4 mr-2" /> New User
        </Button>
      )}
      <Button
        onClick={openInviteDialog}
        size="sm"
        className={theme.actionBtn}
        data-testid="admin-invite-btn"
      >
        <UserPlus className="h-4 w-4 mr-2" /> Invite
      </Button>
    </div>
  );

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Org & User Management"
          description="Provision teammates, manage organizations & roles, and audit account activity."
          theme={theme}
          icon={ShieldCheck}
          stats={heroStats}
          actions={heroActions}
          testId="admin-hero"
        />
      )}
    >
      <div className="px-4 md:px-8 lg:px-12 py-6" data-testid="admin-page">
        {/* Recent admin activity — last 5 audit entries (user
            created, role changed, invitation resent, etc.) so admins
            can spot what their peers just did without scrolling to
            the Audit Log tab. The strip auto-hides on a fresh
            install or when the caller can't read audit logs. */}
        <RecentAdminActivityStrip onOpenAuditLog={() => setActiveTab('audit')} />

        {/* Unusual sign-in location alert — auto-hides when there's
            nothing to triage. Clicking "Inspect" opens that user's
            detail slide-over so admins can pivot directly from
            anomaly → investigation → force-sign-out. */}
        <UnusualLoginsStrip
          onInspectUser={(uid) => {
            const u = users.find((x) => x.user_id === uid)
              || { user_id: uid, email: '', name: '', roles: [] };
            setViewingUser(u);
          }}
        />

        {/* Status row — Pending Invitations + Failed-login Activity
            stacked side-by-side. Both tiles default to a compact
            footprint (Failed-login is collapsible) so the row stays
            balanced; expanding Failed-login lets that column grow
            independently without stretching Pending Invitations.
            `items-start` is critical here — without it the grid
            stretches each cell to the tallest tile, defeating the
            point of collapsing. */}
        <div className="mb-6 grid gap-4 md:grid-cols-2 items-start">
          <PendingInvitesTile />
          {/* Failed-login activity is mirrored here from the
              Telemetry page because incident response usually starts
              from the management surface ("who got locked out?") and
              we don't want admins to hop pages just to triage. */}
          <FailedLoginActivityTile />
        </div>

        <div className="mb-6 max-w-md">
          <SeedDemoTile />
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`grid grid-cols-2 ${currentUser?.is_super_admin ? 'sm:grid-cols-5 max-w-3xl' : 'sm:grid-cols-4 max-w-2xl'} w-full mb-6`}>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="admin-tab-users">
              <Users size={16} />
              Users
            </TabsTrigger>
            <TabsTrigger value="organizations" className="flex items-center gap-2" data-testid="admin-tab-orgs">
              <Building2 size={16} />
              Organizations
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="admin-tab-roles">
              <Shield size={16} />
              Roles
            </TabsTrigger>
            {!!currentUser?.is_super_admin && (
              <TabsTrigger value="modules" className="flex items-center gap-2" data-testid="admin-tab-modules">
                <Layers size={16} />
                Modules
              </TabsTrigger>
            )}
            <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="admin-tab-audit">
              <Activity size={16} />
              Audit Log
            </TabsTrigger>
          </TabsList>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <UsersTab
              users={users}
              filteredUsers={filteredUsers}
              organizations={organizations}
              currentUser={currentUser}
              authIsSuperAdmin={authIsSuperAdmin}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              roleFilter={roleFilter}
              setRoleFilter={setRoleFilter}
              selectedOrg={selectedOrg}
              setSelectedOrg={setSelectedOrg}
              setViewingUser={setViewingUser}
              openEditUser={openEditUser}
              setChangeRoleUser={setChangeRoleUser}
              setNewRoleValue={setNewRoleValue}
              handleResendInvitation={handleResendInvitation}
              handleDeleteUser={handleDeleteUser}
              startImpersonation={startImpersonation}
              navigate={navigate}
              pulsingUserId={pulsingUserId}
            />
          </TabsContent>
          
          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <OrgManagementView
              managingOrgId={managingOrgId}
              setManagingOrgId={setManagingOrgId}
              organizations={organizations}
              users={users}
              currentUser={currentUser}
              authIsSuperAdmin={authIsSuperAdmin}
              fetchUsers={fetchUsers}
              deleteOrgDialogOpen={deleteOrgDialogOpen}
              setDeleteOrgDialogOpen={setDeleteOrgDialogOpen}
              deleteOrgConfirmText={deleteOrgConfirmText}
              setDeleteOrgConfirmText={setDeleteOrgConfirmText}
              handleDeleteOrg={handleDeleteOrg}
              deletingOrg={deletingOrg}
              pulsingOrgId={pulsingOrgId}
              onOrgSettingsSaved={(savedOrgId) => setLastSavedOrgId(savedOrgId)}
              onBackToOrgList={() => {
                if (lastSavedOrgId && lastSavedOrgId === managingOrgId) {
                  setPulsingOrgId(lastSavedOrgId);
                  setTimeout(() => setPulsingOrgId(null), 2500);
                }
                setLastSavedOrgId(null);
                setManagingOrgId(null);
              }}
            />
          </TabsContent>
          

          {/* Roles Tab */}
          <TabsContent value="roles">
            <RolesTab />
          </TabsContent>

          {/* Functional Modules Tab — super-admin only matrix of
              org × role × module enablement. */}
          {!!currentUser?.is_super_admin && (
            <TabsContent value="modules">
              <ModulesTab />
            </TabsContent>
          )}
          
          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <AuditLogTab
              auditSearch={auditSearch}
              setAuditSearch={setAuditSearch}
              auditCategory={auditCategory}
              setAuditCategory={setAuditCategory}
              auditDateRange={auditDateRange}
              setAuditDateRange={setAuditDateRange}
              auditPage={auditPage}
              setAuditPage={setAuditPage}
              auditLogs={auditLogs}
              auditTotal={auditTotal}
              auditStats={auditStats}
              auditCategories={auditCategories}
              fetchAuditLogs={fetchAuditLogs}
            />
          </TabsContent>
        </Tabs>
        
        {/* Invite User Dialog */}
        <InviteUserDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          inviteForm={inviteForm}
          setInviteForm={setInviteForm}
          onSubmit={handleInviteUser}
          isSuperAdmin={!!currentUser?.is_super_admin}
          organizations={organizations}
          onViewOrgSettings={!currentUser?.is_super_admin ? () => {
            const targetOrgId = inviteForm.organization_id
              || currentUser?.organizations?.[0]?.id;
            setInviteDialogOpen(false);
            setActiveTab('organizations');
            setManagingOrgId(null);
            if (targetOrgId) {
              setPulsingOrgId(targetOrgId);
              setTimeout(() => setPulsingOrgId(null), 2500);
            }
          } : undefined}
        />

        {/* Create Organization Dialog */}
        <CreateOrgDialog
          open={createOrgDialogOpen}
          onOpenChange={setCreateOrgDialogOpen}
          orgForm={orgForm}
          setOrgForm={setOrgForm}
          onSubmit={handleCreateOrg}
        />

        {/* Create User Dialog */}
        <CreateUserDialog
          open={createUserDialogOpen}
          onOpenChange={setCreateUserDialogOpen}
          userForm={userForm}
          setUserForm={setUserForm}
          onSubmit={handleCreateUser}
          isSuperAdmin={!!currentUser?.is_super_admin}
          organizations={organizations}
          onViewOrgSettings={!currentUser?.is_super_admin ? () => {
            const targetOrgId = userForm.organization_id
              || currentUser?.organizations?.[0]?.id;
            setCreateUserDialogOpen(false);
            setActiveTab('organizations');
            setManagingOrgId(null);
            if (targetOrgId) {
              setPulsingOrgId(targetOrgId);
              setTimeout(() => setPulsingOrgId(null), 2500);
            }
          } : undefined}
        />

        {/* User Detail Slide-Over (replaces the legacy ViewUserDialog
            with a richer right-side panel showing roles, sessions,
            audit trail, and inline quick-actions). */}
        <UserDetailSlideOver
          viewingUser={viewingUser}
          onClose={() => setViewingUser(null)}
          organizations={organizations}
          roleColors={ROLE_COLORS}
          roleNames={ROLE_NAMES}
          currentUser={currentUser}
          authIsSuperAdmin={authIsSuperAdmin}
          onEdit={(u) => { openEditUser(u); setViewingUser(null); }}
          onChangeRole={(u) => {
            setChangeRoleUser(u);
            setNewRoleValue(u.roles?.[0]?.role || 'user');
            setViewingUser(null);
          }}
          onResendInvite={(u) => { handleResendInvitation(u); }}
          onImpersonate={(u) => {
            const orgId = u.roles?.[0]?.organization_id;
            const orgName = organizations.find((o) => o.id === orgId)?.name || orgId;
            startImpersonation({ ...u, organization_id: orgId, organization_name: orgName });
            setViewingUser(null);
            navigate('/');
          }}
          onUserChanged={fetchUsers}
          onForceSignedOut={(userId) => {
            setViewingUser(null);
            pulseUserRow(userId);
          }}
        />

        {/* Change Role Dialog */}
        <ChangeRoleDialog
          changeRoleUser={changeRoleUser}
          onClose={() => setChangeRoleUser(null)}
          newRoleValue={newRoleValue}
          setNewRoleValue={setNewRoleValue}
          onSubmit={handleChangeRole}
          isSuperAdmin={!!currentUser?.is_super_admin}
        />

        {/* Edit User Dialog */}
        <EditUserDialog
          editingUser={editingUser}
          editForm={editForm}
          setEditForm={setEditForm}
          onClose={() => setEditingUser(null)}
          onSave={handleEditUser}
          editSaving={editSaving}
          organizations={organizations}
          currentUserIsSuperAdmin={!!currentUser?.is_super_admin}
        />

        {/* Activity Timeline overlay — last 24h scrub. Click any
            event to jump to the affected user/org and pulse it. */}
        <AdminActivityTimelineOverlay
          open={timelineOpen}
          onClose={() => setTimelineOpen(false)}
          onNavigate={(intent) => {
            setTimelineOpen(false);
            if (intent.surface === 'user') {
              pulseUserRow(intent.id);
            } else if (intent.surface === 'org') {
              setActiveTab('organizations');
              setManagingOrgId(null);
              setPulsingOrgId(intent.id);
              setTimeout(() => setPulsingOrgId(null), 2500);
            }
          }}
        />
      </div>
    </PageShell>
  );
}
